import { z } from 'zod';
import { contentIdSchema } from '../rules/content-id.js';
import { resourceIdSchema } from '../content/resource.js';
import { sheetResourceSchema } from '../rules/resources.js';
import { hpStateSchema } from '../rules/combat.js';
import { activeConditionSchema, xpStateSchema } from '../rules/progress.js';
import { rollEntrySchema, rollReasonSchema } from '../rules/dice.js';
import { contentRefSchema, seatLabelSchema } from './schemas.js';
/**
 * Панель мастера (D3, D10).
 *
 * Мастер не правит лист: он отдаёт команды — «снять семь хитов»,
 * «выдать кольчугу», «повесить отравление», «начислить опыт».
 * Каждая команда проходит те же правила, что и действие игрока,
 * ложится шагом журнала персонажа (D4) и становится событием
 * кампании (D2). Прямой правки чисел нет ни одной, и это не строгость
 * ради строгости: правка мимо правил — это лист, который нельзя
 * пересчитать и нельзя объяснить.
 */
const uuid = z.uuid();
/* ── Сводка партии ───────────────────────────────────────────────── */
/**
 * Персонаж в сводке мастера.
 *
 * Ровно то, на что смотрят за столом: кто это, сколько у него хитов,
 * во что он одет, что на нём висит и как близко повышение. Полный
 * лист мастер откроет отдельно — здесь его показывать негде
 * и незачем.
 */
/**
 * Ссылка на запись контента: ключ и имя из источника.
 *
 * Определена рядом с кампаниями (`contentRefSchema`) и переэкспортом
 * стоит здесь: одна форма на две панели, второе объявление разошлось
 * бы с первым.
 */
export const recordRefSchema = contentRefSchema;
export const partyMemberSchema = z.strictObject({
    characterId: uuid,
    name: z.string().min(1),
    seatId: uuid,
    seatLabel: seatLabelSchema,
    /** Вид и класс ссылками на записи; `null` — выбора в журнале нет. */
    species: recordRefSchema.nullable(),
    characterClass: recordRefSchema.nullable(),
    level: z.number().int().positive(),
    hp: hpStateSchema,
    /** КД листа; `null` — величины нет вовсе (лист без правил создания). */
    ac: z.number().int().nullable(),
    /** Пассивное Восприятие (с. 21); `null` — величины нет. */
    passivePerception: z.number().int().nullable(),
    conditions: z.array(activeConditionSchema),
    xp: xpStateSchema,
    /**
     * Счётчики ресурсов (D56): мастеру они нужны в сводке ровно затем
     * же, зачем хиты, — за столом спрашивают «ярости остались?»,
     * и открывать ради этого чужой лист незачем.
     */
    resources: z.array(sheetResourceSchema),
});
export const partyInputSchema = z.strictObject({ campaignId: uuid });
export const partyOutputSchema = z.array(partyMemberSchema);
/* ── Команды мастера ─────────────────────────────────────────────── */
/**
 * Команда мастера над персонажем.
 *
 * Разбор по `kind`, а не «действие плюс произвольные поля»: у выдачи
 * предмета и у начисления опыта нет ни одного общего поля, и общая
 * форма означала бы `unknown` в обработчике.
 */
export const masterCommandSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('damage'),
        characterId: uuid,
        amount: z.number().int().nonnegative(),
    }),
    z.strictObject({
        kind: z.literal('heal'),
        characterId: uuid,
        amount: z.number().int().nonnegative(),
    }),
    /** Временные хиты задаются целиком: они не складываются (с. 18). */
    z.strictObject({
        kind: z.literal('temp'),
        characterId: uuid,
        amount: z.number().int().nonnegative(),
    }),
    z.strictObject({
        kind: z.literal('give'),
        characterId: uuid,
        item: contentIdSchema,
        quantity: z.number().int().positive(),
    }),
    z.strictObject({
        kind: z.literal('take'),
        characterId: uuid,
        item: contentIdSchema,
        quantity: z.number().int().positive(),
    }),
    z.strictObject({
        kind: z.literal('condition'),
        characterId: uuid,
        condition: contentIdSchema,
        /** Уровень накопительного состояния; `null` — следующий по счёту. */
        level: z.number().int().positive().nullable(),
    }),
    z.strictObject({
        kind: z.literal('uncondition'),
        characterId: uuid,
        condition: contentIdSchema,
    }),
    z.strictObject({
        kind: z.literal('xp'),
        characterId: uuid,
        amount: z.number().int(),
    }),
    /**
     * Счётчик ресурса на чужом листе (D56): мастер тратит и возвращает
     * его так же, как двигает хиты. Своих счётчиков мастер не заводит:
     * счётчик персонажа заводит тот, чей это персонаж.
     */
    z.strictObject({
        kind: z.literal('resource'),
        characterId: uuid,
        resource: resourceIdSchema,
        /** Отрицательное — вернуть, положительное — потратить. */
        amount: z
            .number()
            .int()
            .refine((value) => value !== 0, 'ноль ничего не меняет'),
    }),
]);
export const masterCommandInputSchema = z.strictObject({
    campaignId: uuid,
    command: masterCommandSchema,
});
/** Ответ команды: обновлённая строка сводки, а не весь лист. */
export const masterCommandResultSchema = z.strictObject({
    member: partyMemberSchema,
});
/* ── Отдых объявляет мастер ──────────────────────────────────────── */
/**
 * Отдых на всю партию (D3).
 *
 * За столом отдыхают вместе, и «каждый нажимает свою кнопку» —
 * это партия, у которой половина отдохнула, а половина забыла.
 * Личный отдых у драфта остаётся: там партии нет.
 */
export const declareRestInputSchema = z.strictObject({
    campaignId: uuid,
    kind: z.enum(['short', 'long']),
});
export const declareRestResultSchema = z.strictObject({
    /** Кому применили: столько персонажей сидит за столом. */
    applied: z.number().int().nonnegative(),
    party: z.array(partyMemberSchema),
});
/* ── Экран боя по инициативе (D10) ───────────────────────────────── */
/**
 * Строка инициативы.
 *
 * Персонажа может не быть: мастеру нужна строка на волка и на трёх
 * гоблинов, а записей существ в приложении нет и в этой фазе
 * не появится. Такая строка живёт подписью и числом — этого хватает,
 * чтобы очередь была общей.
 */
/**
 * Атака монстра в очереди боя (D50).
 *
 * Числа приходят из записи контента и на строку не копируются:
 * строка знает ключ монстра, а бонус и урон читаются из записи
 * в момент показа. Скопировать их значило бы завести второй
 * источник чисел, который однажды разойдётся с первым.
 */
export const monsterAttackRefSchema = z.strictObject({
    key: z.string().min(1),
    name: z.string().min(1),
    bonus: z.number().int(),
    /** Урон записью костей для показа: «2d6 + 3 рубящего». */
    damage: z.array(z.strictObject({
        dice: z.string().nullable(),
        average: z.number().int().nonnegative(),
        type: z.string().min(1),
    })),
});
/**
 * Монстр, стоящий в очереди боя.
 *
 * Строка ссылается на запись контента и хранит только то, что
 * принадлежит этому бою: хиты, КД, состояния и порядковый номер
 * (D50). Правка записи контента идущий бой не меняет — числа боя
 * лежат здесь.
 *
 * Журнала решений у монстра нет: он не растёт, не выбирает
 * и не помнит прошлый бой. Всё его состояние — эти поля, и живут
 * они в кампании, а не в персонаже (D3, D4, D50).
 */
export const monsterRowSchema = z.strictObject({
    /** Ключ записи контента: `srd:wolf`. */
    id: contentIdSchema,
    /**
     * Который это по счёту монстр того же вида в этом бою: «волк
     * первый», «волк второй». Номера не переприсваиваются при удалении
     * строки — иначе второй волк стал бы первым посреди боя.
     */
    ordinal: z.number().int().positive(),
    ac: z.number().int().positive(),
    hp: z.strictObject({
        current: z.number().int(),
        max: z.number().int().positive(),
    }),
    /** Состояния ключами записей: те же, что вешают персонажу. */
    conditions: z.array(contentIdSchema),
    attacks: z.array(monsterAttackRefSchema),
});
export const initiativeRowSchema = z.strictObject({
    id: uuid,
    /** Персонаж за столом; `null` — существо, которого у нас нет. */
    characterId: uuid.nullable(),
    /** Монстр из справочника (D50); `null` — строка мастера или персонаж. */
    monster: monsterRowSchema.nullable(),
    label: z.string().min(1).max(120),
    /** Выпавшая инициатива; `null` — ещё не бросали. */
    value: z.number().int().nullable(),
    /**
     * Чем разводить равные броски. Модификатор Ловкости у персонажа,
     * ноль у строки без него: правило источника ничьи оставляет на
     * усмотрение стола, а очередь обязана быть одинаковой у всех
     * экранов — иначе спор о том, чей ход, начинается заново каждый
     * круг.
     */
    tiebreak: z.number().int(),
});
/**
 * Состояние боя.
 *
 * Круг нулевой означает «бой не идёт»: отдельного флага не нужно,
 * а два признака одного и того же однажды разойдутся.
 */
export const initiativeStateSchema = z.strictObject({
    round: z.number().int().nonnegative(),
    /** Чей ход: номер строки в уже упорядоченном списке. */
    turn: z.number().int().nonnegative(),
    /** Строки в порядке очереди — том самом, который видят все. */
    rows: z.array(initiativeRowSchema),
});
export const initiativeInputSchema = z.strictObject({ campaignId: uuid });
/**
 * Начало боя.
 *
 * `rollAll` — мастер бросает за всех сразу: за столом так быстрее,
 * и это его право. Иначе строки заводятся пустыми, и каждый бросает
 * себе сам — тем же роллером и в тот же общий журнал (D12).
 */
export const startCombatInputSchema = z.strictObject({
    campaignId: uuid,
    rollAll: z.boolean(),
});
export const addInitiativeRowInputSchema = z.strictObject({
    campaignId: uuid,
    label: z.string().trim().min(1).max(120),
    /** Инициатива существа: мастер вносит её сам или бросает роллером. */
    value: z.number().int().nullable(),
});
export const removeInitiativeRowInputSchema = z.strictObject({
    campaignId: uuid,
    rowId: uuid,
});
/**
 * Монстр в справочнике мастера.
 *
 * Список, а не каталог: на экране выбора нужны имя, опасность
 * и пара чисел, а полные статблоки трёх сотен монстров — мегабайты
 * на каждое открытие панели. То же решение, что и у предметов.
 */
export const monsterListEntrySchema = z.strictObject({
    id: contentIdSchema,
    name: z.string().min(1),
    /** Опасность как напечатано: «1/4», «30». */
    challenge: z.string().min(1),
    /** Числовая опасность: по ней сортируется список. */
    challengeValue: z.number().nonnegative(),
    ac: z.number().int().positive(),
    hp: z.number().int().positive(),
    creatureType: z.string().min(1),
});
export const monsterListInputSchema = z.strictObject({ campaignId: uuid });
export const monsterListOutputSchema = z.strictObject({
    monsters: z.array(monsterListEntrySchema),
});
/**
 * Добавить монстра в очередь.
 *
 * Мастер выбирает запись, а не печатает имя (D50): напечатанное имя
 * не даёт ни хитов, ни КД, ни атак, и бросать за такую строку нечем.
 */
export const addMonsterInputSchema = z.strictObject({
    campaignId: uuid,
    monsterId: contentIdSchema,
    /** Сколько сразу: «три гоблина» — обычный случай за столом. */
    count: z.number().int().positive().max(12),
});
/**
 * Хиты монстра.
 *
 * Те же три действия, что у персонажа (`masterCommandSchema`), но
 * путь другой: у персонажа они ложатся шагом журнала (D4), а у
 * монстра журнала нет — меняется строка боя.
 */
export const monsterHpInputSchema = z.strictObject({
    campaignId: uuid,
    rowId: uuid,
    action: z.strictObject({
        kind: z.enum(['damage', 'heal']),
        amount: z.number().int().positive().max(999),
    }),
});
export const monsterConditionInputSchema = z.strictObject({
    campaignId: uuid,
    rowId: uuid,
    condition: contentIdSchema,
    /** `true` — повесить, `false` — снять. */
    on: z.boolean(),
});
/**
 * Бросок атаки монстра.
 *
 * Бросок и урон уходят двумя записями в общий журнал (D12): за
 * столом сначала объявляют попадание, потом урон, и в ленте они
 * тоже должны стоять порознь.
 */
export const rollMonsterAttackInputSchema = z.strictObject({
    campaignId: uuid,
    rowId: uuid,
    attackKey: z.string().min(1),
    /** Скрытый бросок мастера: в ленту уходит отметка без числа. */
    hidden: z.boolean(),
});
export const rollInitiativeInputSchema = z.strictObject({
    campaignId: uuid,
    /** Чью строку бросаем; `null` — свою (игрок бросает за себя). */
    rowId: uuid.nullable(),
});
export const initiativeResultSchema = z.strictObject({
    initiative: initiativeStateSchema,
});
/* ── Общий журнал бросков (D12) ──────────────────────────────────── */
/**
 * Бросок за столом.
 *
 * Скрытый бросок мастера доезжает до всех, но без результата: в ленте
 * остаётся отметка, что бросок был. Прятать сам факт нельзя — тогда
 * мастер перестаёт быть частью стола; показывать число — значит
 * отменить смысл скрытого броска.
 */
export const campaignRollSchema = z.strictObject({
    id: uuid,
    at: z.iso.datetime(),
    /** Кто бросал: имя персонажа или подпись строки инициативы. */
    who: z.string().min(1),
    characterId: uuid.nullable(),
    reason: rollReasonSchema,
    /** Результат; `null` — бросок скрыт и виден только мастеру. */
    outcome: rollEntrySchema.shape.outcome.nullable(),
    hidden: z.boolean(),
});
export const campaignRollsInputSchema = z.strictObject({
    campaignId: uuid,
    limit: z.number().int().positive().max(200).default(50),
});
export const campaignRollsOutputSchema = z.array(campaignRollSchema);
/* ── Что мастер может выдать и повесить ──────────────────────────── */
/**
 * Запись контента в выборе мастера: идентификатор и имя.
 *
 * Ни текста, ни механики: панели нужен список, а не каталог.
 * Предметов четыре с половиной сотни, и возить их описания ради
 * выпадающего списка — это мегабайт на каждое открытие панели.
 */
export const contentChoiceRowSchema = z.strictObject({
    id: contentIdSchema,
    name: z.string().min(1),
});
export const masterCatalogueSchema = z.strictObject({
    items: z.array(contentChoiceRowSchema),
    /** Состояния вместе с тем, накопительные ли они (глоссарий). */
    conditions: z.array(contentChoiceRowSchema.extend({
        cumulative: z.boolean(),
        maxLevel: z.number().int().positive().nullable(),
    })),
});
export const masterCatalogueInputSchema = z.strictObject({ campaignId: uuid });
//# sourceMappingURL=master.js.map